import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import { ContentCopy, ContentPaste, ContentCopyOutlined, LockOpen, LockClock, Schedule, Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { WeeklyHours, DayOfWeek, DayHours } from '../../types/common.types';
import { Shop } from '../../types/shop.types';
import TimePicker24 from '../common/TimePicker24';

interface DefaultHoursEditorProps {
  open: boolean;
  defaultHours: WeeklyHours;
  shopName: string;
  onClose: () => void;
  onSave: (hours: WeeklyHours) => Promise<void>;
  isLoading?: boolean;
  allShops?: Shop[]; // All shops for copying from other shops
  currentShopId?: string; // Current shop ID to exclude from list
}

const daysOfWeek: Array<{ key: DayOfWeek; label: string }> = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export default function DefaultHoursEditor({
  open,
  defaultHours,
  shopName,
  onClose,
  onSave,
  isLoading,
  allShops = [],
  currentShopId,
}: DefaultHoursEditorProps) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [hours, setHours] = useState<WeeklyHours>(defaultHours);
  const [copiedDay, setCopiedDay] = useState<DayHours | null>(null);
  const [copyFromShopDialogOpen, setCopyFromShopDialogOpen] = useState(false);
  const [copiedFromDay, setCopiedFromDay] = useState<DayOfWeek | null>(null);

  const handleToggleDay = (day: DayOfWeek) => {
    setHours({
      ...hours,
      [day]: {
        ...hours[day],
        isOpen: !hours[day].isOpen,
      },
    });
  };

  const handleTimeChange = (day: DayOfWeek, field: 'openTime' | 'closeTime', value: string) => {
    setHours({
      ...hours,
      [day]: {
        ...hours[day],
        [field]: value,
      },
    });
  };

  const handleSave = async () => {
    await onSave(hours);
  };

  const handleClose = () => {
    setHours(defaultHours); // Reset to original
    setCopiedDay(null);
    setCopiedFromDay(null);
    setCopyFromShopDialogOpen(false);
    onClose();
  };

  const handleSetAllDays = (isOpen: boolean) => {
    const updatedHours = { ...hours };
    daysOfWeek.forEach(({ key }) => {
      updatedHours[key] = {
        ...updatedHours[key],
        isOpen,
      };
    });
    setHours(updatedHours);
  };

  // Copy a day's hours
  const handleCopyDay = (day: DayOfWeek) => {
    setCopiedDay({ ...hours[day] });
    setCopiedFromDay(day);
    const dayLabel = daysOfWeek.find((d) => d.key === day)?.label || day;
    enqueueSnackbar(`${dayLabel} hours copied`, { variant: 'info', autoHideDuration: 2000 });
  };

  // Clear copied data
  const handleClearCopy = () => {
    setCopiedDay(null);
    setCopiedFromDay(null);
    enqueueSnackbar('Copied hours cleared', { variant: 'info', autoHideDuration: 2000 });
  };

  // Paste copied hours to a day
  const handlePasteDay = (day: DayOfWeek) => {
    if (copiedDay && copiedFromDay) {
      const previousState = { ...hours[day] };
      const fromDayLabel = daysOfWeek.find((d) => d.key === copiedFromDay)?.label || copiedFromDay;
      const toDayLabel = daysOfWeek.find((d) => d.key === day)?.label || day;

      setHours({
        ...hours,
        [day]: { ...copiedDay },
      });

      // Show snackbar with undo option
      enqueueSnackbar(
        `Copied hours from ${fromDayLabel} to ${toDayLabel}`,
        {
          variant: 'success',
          autoHideDuration: 5000,
          action: (key) => (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                // Undo the paste
                setHours((currentHours) => ({
                  ...currentHours,
                  [day]: previousState,
                }));
                closeSnackbar(key);
                enqueueSnackbar('Paste undone', { variant: 'info', autoHideDuration: 2000 });
              }}
            >
              Undo
            </Button>
          ),
        }
      );
    }
  };

  // Copy hours from another shop
  const handleCopyFromShop = (shopId: string) => {
    const shop = allShops.find((s) => s.id === shopId);
    if (shop && shop.defaultHours) {
      setHours({ ...shop.defaultHours });
    }
    setCopyFromShopDialogOpen(false);
  };

  // Filter out current shop from the list
  const otherShops = allShops.filter((shop) => shop.id !== currentShopId);

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Schedule color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Default Weekly Hours
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Set the default operating hours for <strong>{shopName}</strong>
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Quick Actions at top */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 0.5 }}>
            QUICK ACTIONS:
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => handleSetAllDays(true)}
            disabled={isLoading}
            startIcon={<LockOpen fontSize="small" />}
            sx={{ minWidth: 'auto' }}
          >
            Open All
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            size="small"
            variant="text"
            onClick={() => handleSetAllDays(false)}
            disabled={isLoading}
            startIcon={<LockClock fontSize="small" />}
            sx={{ minWidth: 'auto' }}
          >
            Close All
          </Button>
          {otherShops.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Button
                size="small"
                variant="text"
                onClick={() => setCopyFromShopDialogOpen(true)}
                disabled={isLoading}
                startIcon={<ContentCopyOutlined fontSize="small" />}
                sx={{ minWidth: 'auto' }}
              >
                Copy from Shop
              </Button>
            </>
          )}
        </Box>

        {/* Copied indicator banner */}
        {copiedDay && copiedFromDay && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'info.50',
              border: '1px solid',
              borderColor: 'info.200',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ContentCopy color="info" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>
                {daysOfWeek.find((d) => d.key === copiedFromDay)?.label} hours copied
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({copiedDay.isOpen ? `${copiedDay.openTime} - ${copiedDay.closeTime}` : 'Closed'})
              </Typography>
            </Box>
            <Tooltip title="Clear copied hours">
              <IconButton size="small" onClick={handleClearCopy}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        )}

        {/* Days list */}
        <Stack spacing={1.5}>
          {daysOfWeek.map(({ key, label }) => (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                transition: 'all 0.2s',
                bgcolor: hours[key].isOpen ? 'background.paper' : 'grey.50',
                borderLeft: hours[key].isOpen ? 4 : 0,
                borderLeftColor: 'primary.main',
                '&:hover': {
                  boxShadow: 1,
                },
                // Highlight the source day
                ...(copiedFromDay === key && {
                  bgcolor: 'info.50',
                  borderColor: 'info.main',
                }),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                {/* Day name and switch */}
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      flex: 1,
                      color: hours[key].isOpen ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {label}
                  </Typography>
                  <Switch
                    checked={hours[key].isOpen}
                    onChange={() => handleToggleDay(key)}
                    disabled={isLoading}
                  />
                </Box>

                {/* Time pickers */}
                {hours[key].isOpen ? (
                  <>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <TimePicker24
                        label="Opens"
                        value={hours[key].openTime}
                        onChange={(value) => handleTimeChange(key, 'openTime', value)}
                        disabled={isLoading}
                        size="small"
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <TimePicker24
                        label="Closes"
                        value={hours[key].closeTime}
                        onChange={(value) => handleTimeChange(key, 'closeTime', value)}
                        disabled={isLoading}
                        size="small"
                        fullWidth
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Closed
                    </Typography>
                  </Box>
                )}

                {/* Copy/Paste actions */}
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopyDay(key)}
                      disabled={isLoading}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      copiedDay && copiedFromDay
                        ? `Paste hours from ${daysOfWeek.find((d) => d.key === copiedFromDay)?.label}`
                        : "Copy a day first"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handlePasteDay(key)}
                        disabled={!copiedDay || isLoading}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <ContentPaste fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>

      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          size="large"
          sx={{ minWidth: 120 }}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Copy from Shop Dialog */}
    <Dialog
      open={copyFromShopDialogOpen}
      onClose={() => setCopyFromShopDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              bgcolor: 'primary.50',
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ContentCopyOutlined color="primary" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Copy Hours from Shop
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select a shop to copy all hours
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          {otherShops.map((sourceShop) => {
            const openDaysCount = Object.values(sourceShop.defaultHours).filter(
              (dayHours) => dayHours.isOpen
            ).length;
            return (
              <Paper
                key={sourceShop.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
                onClick={() => handleCopyFromShop(sourceShop.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: sourceShop.color || 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Schedule sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {sourceShop.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Open {openDaysCount} {openDaysCount === 1 ? 'day' : 'days'}/week
                    </Typography>
                  </Box>
                  <ContentCopy sx={{ color: 'action.active', fontSize: 20 }} />
                </Box>
              </Paper>
            );
          })}
          {otherShops.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No other shops available to copy from.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Create more shops to use this feature.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setCopyFromShopDialogOpen(false)} size="large">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
